import React from 'react';
import { Box, LinearProgress, Typography, CircularProgress, Fade } from '@mui/material';
import { keyframes } from '@mui/system';

// Pulse animation for loading text
const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

// Floating animation for the logo/icon
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const LoadingScreen = ({ message = "Loading your dashboard...", showProgress = false, progress = 0 }) => {
  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          zIndex: 9999,
          gap: 3
        }}
      >
        {/* Animated Logo/Icon */}
        <Box
          sx={{
            animation: `${float} 3s ease-in-out infinite`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h3" color="primary.contrastText" fontWeight={900}>
              M
            </Typography>
            
            {/* Animated ring */}
            <Box
              sx={{
                position: 'absolute',
                top: -5,
                left: -5,
                right: -5,
                bottom: -5,
                border: '3px solid',
                borderColor: 'primary.light',
                borderRadius: '50%',
                borderTopColor: 'transparent',
                animation: 'spin 1.5s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
          </Box>

          <Typography 
            variant="h6" 
            fontWeight={700} 
            color="primary.main"
            sx={{
              animation: `${pulse} 2s ease-in-out infinite`
            }}
          >
            Mover Portal
          </Typography>
        </Box>

        {/* Loading Message */}
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{
            animation: `${pulse} 2s ease-in-out infinite`,
            animationDelay: '0.5s'
          }}
        >
          {message}
        </Typography>

        {/* Progress Bar (optional) */}
        {showProgress && (
          <Box sx={{ width: '300px', maxWidth: '80vw' }}>
            <LinearProgress 
              variant={progress > 0 ? "determinate" : "indeterminate"}
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3
                }
              }}
            />
            {progress > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {Math.round(progress)}%
              </Typography>
            )}
          </Box>
        )}

        {/* Spinning loader as backup */}
        <CircularProgress 
          size={24} 
          thickness={4}
          sx={{ 
            opacity: 0.6,
            mt: 1
          }}
        />
      </Box>
    </Fade>
  );
};

// Mini loading component for inline use
export const MiniLoader = ({ size = 20, color = "primary" }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
    <CircularProgress size={size} color={color} thickness={4} />
  </Box>
);

// Loading overlay for components
export const LoadingOverlay = ({ loading, children, message = "Loading..." }) => (
  <Box sx={{ position: 'relative' }}>
    {children}
    {loading && (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 10,
          backdropFilter: 'blur(2px)'
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {message}
        </Typography>
      </Box>
    )}
  </Box>
);

export default LoadingScreen;